import { FileText, Scale, Users, Ban } from 'lucide-react';
import LegalDocumentLayout from '@/components/LegalDocumentLayout';

const sections = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    shortTitle: 'Acceptance',
    summary: 'Agreement required to use our services.',
    content:
      'By accessing vibhumedusys.com or using VibhumEduSys school management software and related services ("Services"), you agree to be bound by these Terms and Conditions. If you are accepting on behalf of a school, college, or other organization, you represent that you have authority to bind that organization.',
    points: ['If you do not agree, please do not use our website or Services.'],
  },
  {
    id: 'description',
    title: '2. Description of Services',
    shortTitle: 'Services',
    summary: 'What VibhumEduSys provides to institutions.',
    content:
      'VibhumEduSys provides digital tools for educational institutions, including student information management, attendance, fees, academics, communication, websites, and related modules.',
    points: [
      'Available features depend on your subscribed plan or package.',
      'We may update, enhance, or discontinue features with reasonable notice.',
    ],
  },
  {
    id: 'accounts',
    title: '3. Accounts & Registration',
    shortTitle: 'Accounts',
    summary: 'Your responsibilities for account access.',
    content:
      'To use certain Services you must register and provide accurate, complete information. You are responsible for maintaining the confidentiality of login credentials and for all activity under your account.',
    points: [
      'Notify us promptly of any unauthorized use.',
      'Institutions are responsible for managing staff, student, and parent access.',
    ],
  },
  {
    id: 'acceptable-use',
    title: '4. Acceptable Use',
    shortTitle: 'Use rules',
    summary: 'Guidelines for fair and lawful platform use.',
    content:
      'You agree not to misuse the Services. Prohibited conduct includes attempting unauthorized access, uploading malicious code, reverse engineering except where permitted by law, unlawful use, harassment, or interfering with platform integrity.',
    points: ['We may suspend or terminate access for violations.'],
  },
  {
    id: 'institutional-data',
    title: '5. Institutional Data & Content',
    shortTitle: 'Data',
    summary: 'Ownership and processing of your institution data.',
    content:
      'You retain ownership of data and content you submit to VibhumEduSys. By using the Services, you grant us a limited license to host, process, and display that data solely as needed to provide and improve the Services.',
    points: [
      'You must have legal rights to upload and process student or staff information.',
    ],
  },
  {
    id: 'fees',
    title: '6. Fees & Payment',
    shortTitle: 'Fees',
    summary: 'Billing terms for paid subscriptions.',
    content:
      'Paid plans are subject to the pricing, billing cycle, and payment terms communicated at purchase or in your agreement with us. Fees are generally non-refundable except where required by law or expressly stated otherwise.',
    points: [
      'Pricing may change with prior notice for subsequent billing periods.',
    ],
  },
  {
    id: 'intellectual-property',
    title: '7. Intellectual Property',
    shortTitle: 'IP',
    summary: 'Rights related to VibhumEduSys branding and software.',
    content:
      'VibhumEduSys, including its software, branding, logos, documentation, and website content, is owned by us or our licensors and is protected by intellectual property laws. You may not copy, modify, distribute, or create derivative works except as expressly permitted.',
    points: ['Feedback you provide may be used by us without obligation to you.'],
  },
  {
    id: 'third-party',
    title: '8. Third-Party Services',
    shortTitle: 'Third-party',
    summary: 'External tools linked or integrated with our platform.',
    content:
      'Our website or platform may link to or integrate with third-party services such as payment gateways, app stores, or communication tools. We are not responsible for third-party content, policies, or practices.',
    points: ['Your use of third-party services is governed by their own terms.'],
  },
  {
    id: 'disclaimer',
    title: '9. Disclaimer of Warranties',
    shortTitle: 'Disclaimer',
    summary: 'Service availability and warranty limits.',
    content:
      'The Services are provided on an "as is" and "as available" basis. To the fullest extent permitted by law, we disclaim warranties of merchantability, fitness for a particular purpose, and non-infringement.',
    points: [
      'We do not warrant uninterrupted, error-free, or completely secure operation.',
    ],
  },
  {
    id: 'liability',
    title: '10. Limitation of Liability',
    shortTitle: 'Liability',
    summary: 'Limits on damages and claims.',
    content:
      'To the maximum extent permitted by law, VibhumEduSys and its affiliates shall not be liable for indirect, incidental, special, consequential, or punitive damages, or for loss of profits, data, or goodwill arising from your use of the Services.',
    points: [
      'Total liability is limited to fees paid in the twelve months preceding the claim.',
    ],
  },
  {
    id: 'termination',
    title: '11. Termination',
    shortTitle: 'Termination',
    summary: 'When access to services may end.',
    content:
      'You may stop using the Services at any time. We may suspend or terminate access if you breach these Terms, fail to pay applicable fees, or if we discontinue the Services.',
    points: [
      'Surviving provisions include IP, disclaimers, and liability limitations.',
    ],
  },
  {
    id: 'governing-law',
    title: '12. Governing Law',
    shortTitle: 'Law',
    summary: 'Legal jurisdiction for disputes.',
    content:
      'These Terms are governed by the laws of India. Courts in New Delhi, India shall have exclusive jurisdiction over disputes arising from these Terms or the Services, subject to any mandatory consumer protections that may apply.',
  },
  {
    id: 'changes',
    title: '13. Changes to These Terms',
    shortTitle: 'Updates',
    summary: 'How revised terms take effect.',
    content:
      'We may revise these Terms and Conditions periodically. The "Last updated" date will reflect the latest version. Material changes will be communicated where reasonable. Continued use after changes take effect means you accept the updated Terms.',
  },
  {
    id: 'contact',
    title: '14. Contact',
    shortTitle: 'Contact',
    summary: 'Get help understanding these terms.',
    content:
      'For questions about these Terms and Conditions, contact us at vibhumedusys@gmail.com or +91 96256 90427. Our office is at Rohini Sector 21, New Delhi, India.',
  },
];

const highlights = [
  {
    icon: Scale,
    title: 'Clear agreement',
    description: 'Straightforward terms for using VibhumEduSys website and software.',
    targetId: 'acceptance',
  },
  {
    icon: Users,
    title: 'Institutional use',
    description: 'Built for schools and colleges managing staff, students, and parents.',
    targetId: 'accounts',
  },
  {
    icon: Ban,
    title: 'Fair use rules',
    description: 'Guidelines that keep the platform secure and reliable for everyone.',
    targetId: 'acceptable-use',
  },
  {
    icon: FileText,
    title: 'Your data rights',
    description: 'Understand ownership and responsibilities for institutional content.',
    targetId: 'institutional-data',
  },
];

export default function TermsAndConditionPage() {
  return (
    <LegalDocumentLayout
      testId="terms-page"
      title="Terms and Conditions"
      description="The rules and responsibilities that apply when you use VibhumEduSys websites, demos, and school management services."
      lastUpdated="July 10, 2026"
      icon={FileText}
      highlights={highlights}
      sections={sections}
      contactTitle="Need clarification?"
      contactDescription="Contact our team if you have questions about these terms before subscribing or during use."
      secondaryLink={{ to: '/privacy', label: 'Privacy Policy' }}
    />
  );
}
